/**
 * TextMessageAttachment.js
 * Talkroute
 *
 * Created by Vel Genov on 10/3/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import validateArray from "@helpers/arrayValidator";
import FilePathHelper from "@helpers/filePathHelper";

/**
 * A constant that represents the attachment file type
 */
export enum FileType {
  IMAGE = "image",
  TEXT = "text",
}

/**
 * A constant representing the standard attachment size
 */
export const ATTACHMENT_SIZE = {
  width: 1280,
  height: 960,
  jpgQuality: 80,
};

/**
 * A data class representing a text message attachment
 *
 * @param id the message attachment's unique id
 * @param fileType the file type of the attachment
 */
export class TextMessageAttachment {
  public id: number;
  public filePath: string;
  public errorLoading: boolean;
  public fileType: FileType;

  constructor(opts) {
    const { fileType, id } = opts;
    this.id = id;
    this.filePath = "";
    this.errorLoading = false;
    this.fileType = fileType;
  }

  setFilePath = (filePath) => {
    this.filePath = filePath;
  };

  getFilePath = (conversationId) => {
    const { id: attachmentId } = this;
    const filePath = new FilePathHelper().findMessageAttachmentFilePathAndName({
      conversationId,
      attachmentId,
    });

    this.filePath = filePath;
    return filePath;
  };

  hasErrorLoading = (hasError) => {
    this.errorLoading = hasError;
  };
}

/**
 * Parses the incoming JSON text message attachment data. Creates a TextMessageAttachment object for each line item
 *
 * @param {*} textMessageData an array containig text message data.
 */
export const textMessageAttachmentJSONParser = (attachmentData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (validateArray(attachmentData)) {
    const parsedData = attachmentData.reduce((acc, entry) => {
      const { id, file_type: fileType } = entry;

      // Only process if fileType is not 'text'
      if (fileType !== FileType.TEXT) {
        const attachmentEntry = new TextMessageAttachment({
          id,
          fileType,
        });
        acc.push(attachmentEntry);
      }

      return acc;
    }, []);

    return parsedData;
  }
};

/**
 * Parses the incoming generic objects into TextMessageAttachment objects.
 * Checks for empty objects passed in
 *
 * @param {*} attachmentData an array containig text message attachments.
 */
export const textMessageAttachmentRehydrator = (attachmentData) => {
  // If valid data is passed, proceed. Otherwise don't return anything
  if (validateArray(attachmentData)) {
    // Process each entry
    const parsedData = attachmentData.map((entry) =>
      rehydrateTextMessageAttachment(entry)
    );

    return parsedData;
  }
  return [];
};

/**
 * Parses the incoming single generic object into TextMessageAttachment.
 * Checks for an empty object being passed in.
 *
 * @param {*} textMessageAttachment a single generic object containing a text message attacment.
 */
export const rehydrateTextMessageAttachment = (textMessageAttachment) => {
  if (textMessageAttachment) {
    const { id, fileType } = textMessageAttachment;

    const textMessageAttachmentEntry = new TextMessageAttachment({
      id,
      fileType,
    });

    return textMessageAttachmentEntry;
  }
  return textMessageAttachment;
};
